// E8StackedCubesSurfaceAreaClosedTask.js
// Zadanie zamkniete E8 (2026 zad. 14) - trzy jednakowe szesciany ustawione jeden na drugim.

import React from "react";
import { BlockMath, ChoiceOptionMath, InlineMath } from "../../../../components/math/KatexWrappers";
import SvgMathLabel from "../../../../components/diagrams/SvgMathLabel";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function buildOptions(correct, distractors, keepOrder = false) {
  const unique = [];
  [correct, ...distractors].forEach((value) => {
    if (value > 0 && !unique.includes(value)) unique.push(value);
  });
  let extra = correct + 25;
  while (unique.length < 4) {
    if (!unique.includes(extra)) unique.push(extra);
    extra += 25;
  }
  const selected = [correct, ...unique.filter((value) => value !== correct)].slice(0, 4);
  const ordered = (keepOrder ? selected : shuffle(selected)).map((value) => ({ value, label: String(value) }));
  return { options: ordered, correctIndex: ordered.findIndex((option) => option.value === correct) };
}

function buildData({ edge, count, options = null }) {
  const height = edge * count;
  const surfaceArea = 2 * (edge * edge + edge * height + edge * height);
  const builtOptions = options || buildOptions(surfaceArea, [edge * edge * count * 6, surfaceArea + edge * edge, surfaceArea - edge * edge, 2 * edge * height]);
  return { edge, count, height, surfaceArea, options: builtOptions.options, correctIndex: builtOptions.correctIndex };
}

function createOriginalTask() {
  return {
    type: "E8StackedCubesSurfaceAreaClosedTask",
    data: buildData({
      edge: 5,
      count: 3,
      options: { options: [350, 375, 400, 450].map((value) => ({ value, label: String(value) })), correctIndex: 0 },
    }),
    source: { variant: "original", exam: "E8", session: "2026_MAJ", taskNumber: 14 },
    tags: ["e8", "stereometria", "szescian", "prostopadloscian", "pole-powierzchni"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();
  const edge = pick([2, 3, 4, 5, 6, 7, 8]);
  const count = pick([2, 3, 4]);
  return {
    type: "E8StackedCubesSurfaceAreaClosedTask",
    data: buildData({ edge, count }),
    source: { variant: "random", basedOn: "E8_2026_MAJ_14" },
    tags: ["e8", "stereometria", "szescian", "prostopadloscian", "pole-powierzchni"],
  };
}

function CubesDiagram({ data }) {
  const fill = "rgba(255,159,10,0.18)";
  const accent = "#ff9f0a";
  const count = data.count;

  // Oblique projection: front face + depth offset
  const x0 = 78, y0 = 38;
  const w = 68;       // szerokość = wysokość jednego sześcianu → kwadratowa ściana frontowa
  const cubeH = 68;
  const h = count * cubeH;
  const dx = 38, dy = -28; // głębokość pod kątem ~36°

  // Front face corners (clockwise from bottom-left)
  const A = { x: x0, y: y0 + h };
  const B = { x: x0 + w, y: y0 + h };
  const C = { x: x0 + w, y: y0 };
  const D = { x: x0, y: y0 };

  // Back face corners
  const Ap = { x: A.x + dx, y: A.y + dy };
  const Bp = { x: B.x + dx, y: B.y + dy };
  const Cp = { x: C.x + dx, y: C.y + dy };
  const Dp = { x: D.x + dx, y: D.y + dy };

  const divYs = Array.from({ length: count - 1 }, (_, i) => y0 + (i + 1) * cubeH);
  const svgW = x0 + w + dx + 30;
  const svgH = y0 + h + 20;

  return (
    <div style={{ display: "flex", justifyContent: "center", margin: "14px 0" }}>
      <svg width={svgW} height={svgH} viewBox={`0 0 ${svgW} ${svgH}`} role="img" aria-label="Jednakowe sześciany ustawione jeden na drugim" style={{ maxWidth: "100%", height: "auto" }}>
        {/* Hidden edges — dashed */}
        <line x1={A.x} y1={A.y} x2={Ap.x} y2={Ap.y} stroke="currentColor" strokeWidth="1.5" strokeDasharray="5,4" />
        <line x1={Ap.x} y1={Ap.y} x2={Bp.x} y2={Bp.y} stroke="currentColor" strokeWidth="1.5" strokeDasharray="5,4" />
        <line x1={Ap.x} y1={Ap.y} x2={Dp.x} y2={Dp.y} stroke="currentColor" strokeWidth="1.5" strokeDasharray="5,4" />
        {/* Hidden division lines */}
        {divYs.map((yd, i) => (
          <g key={i}>
            <line x1={x0} y1={yd} x2={x0 + dx} y2={yd + dy} stroke="currentColor" strokeWidth="1.5" strokeDasharray="5,4" />
            <line x1={x0 + dx} y1={yd + dy} x2={x0 + w + dx} y2={yd + dy} stroke="currentColor" strokeWidth="1.5" strokeDasharray="5,4" />
          </g>
        ))}
        {/* Visible fills */}
        <rect x={x0} y={y0} width={w} height={h} fill={fill} />
        <polygon points={`${D.x},${D.y} ${C.x},${C.y} ${Cp.x},${Cp.y} ${Dp.x},${Dp.y}`} fill={fill} />
        <polygon points={`${C.x},${C.y} ${B.x},${B.y} ${Bp.x},${Bp.y} ${Cp.x},${Cp.y}`} fill={fill} />
        {/* Visible outlines */}
        <rect x={x0} y={y0} width={w} height={h} fill="none" stroke="currentColor" strokeWidth="2" />
        <polygon points={`${D.x},${D.y} ${C.x},${C.y} ${Cp.x},${Cp.y} ${Dp.x},${Dp.y}`} fill="none" stroke="currentColor" strokeWidth="2" />
        <polygon points={`${C.x},${C.y} ${B.x},${B.y} ${Bp.x},${Bp.y} ${Cp.x},${Cp.y}`} fill="none" stroke="currentColor" strokeWidth="2" />
        {/* Visible division lines */}
        {divYs.map((yd, i) => (
          <g key={i}>
            <line x1={x0} y1={yd} x2={x0 + w} y2={yd} stroke="currentColor" strokeWidth="1.5" />
            <line x1={x0 + w} y1={yd} x2={x0 + w + dx} y2={yd + dy} stroke="currentColor" strokeWidth="1.5" />
          </g>
        ))}
        {/* Edge length marker */}
        <line x1={x0 - 14} y1={y0} x2={x0 - 14} y2={y0 + cubeH} stroke={accent} strokeWidth="3" />
        <text x={x0 - 28} y={y0 + cubeH / 2 + 5} fill={accent} fontSize="16" textAnchor="middle">{data.edge}</text>
      </svg>
    </div>
  );
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        {data.count === 3 ? "Trzy" : data.count} jednakowe sześciany o krawędzi długości <InlineMath math={`${data.edge}`} /> ustawiono jeden na drugim i otrzymano prostopadłościan.
      </p>
      <CubesDiagram data={data} />
      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>
      <p>Pole powierzchni całkowitej otrzymanego prostopadłościanu jest równe</p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8, marginTop: 10 }}>
        {data.options.map((option, index) => <div key={option.value}><ChoiceOptionMath letter={LETTERS[index]} math={`${option.value}`} /></div>)}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  return <ABCDInput options={data.options.map((option, index) => ({ label: LETTERS[index], textNode: LETTERS[index] }))} value={userAnswer} onChange={onChange} disabled={disabled} />;
}

export function checkAnswer({ data }, userAnswer) {
  return userAnswer === data.correctIndex;
}

export function renderAnswer({ data }) {
  return <ChoiceOptionMath letter={LETTERS[data.correctIndex]} math={`${data.surfaceArea}`} />;
}

export function renderUserAnswer(userAnswer, { data }) {
  if (typeof userAnswer !== "number") return <i>(brak odpowiedzi)</i>;
  const option = data.options[userAnswer];
  if (!option) return <i>(nieprawidłowy wybór)</i>;
  return <ChoiceOptionMath letter={LETTERS[userAnswer]} math={`${option.value}`} />;
}

export function renderExplanation({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p><strong>Krok 1.</strong> Otrzymany prostopadłościan ma wymiary:</p>
      <BlockMath math={`${data.edge}\\times ${data.edge}\\times ${data.height}`} />
      <p><strong>Krok 2.</strong> Korzystamy ze wzoru na pole powierzchni prostopadłościanu.</p>
      <BlockMath math={`P_c=2(ab+ac+bc)=2(${data.edge}\\cdot ${data.edge}+${data.edge}\\cdot ${data.height}+${data.edge}\\cdot ${data.height})=${data.surfaceArea}`} />
      <p>Poprawna odpowiedź to <InlineMath math={`\\text{${LETTERS[data.correctIndex]}}`} />.</p>
    </div>
  );
}

